import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ArrowLeft, Instagram, Linkedin, Twitter, Facebook, AtSign, ChevronLeft, ChevronRight } from 'lucide-react';
import Markdown from 'react-markdown';
import { useTranslation } from '../hooks/useTranslation';

interface ShowcaseItem {
  id?: string;
  platform: 'ig' | 'li' | 'x' | 'fb' | 'tr';
  imageUrl: string;
  imageUrls?: string[];
  title: string;
  brandName: string;
  description: string;
  createdAt: any;
}

export function ShowcaseDetail() {
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<ShowcaseItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { isBrazil } = useTranslation();

  const nextImage = () => {
    if (item?.imageUrls && item.imageUrls.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % item.imageUrls.length);
    }
  };

  const prevImage = () => {
    if (item?.imageUrls && item.imageUrls.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + item.imageUrls.length) % item.imageUrls.length);
    }
  };

  useEffect(() => {
    const fetchItem = async () => {
      if (!id) return;
      try {
        const docRef = doc(db, 'showcase_items', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setItem({ id: docSnap.id, ...docSnap.data() } as ShowcaseItem);
        }
      } catch (err) {
        console.error("Error fetching showcase item:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-indigo-600 border-t-transparent animate-spin"></div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-[#030303] pt-32 px-6 flex flex-col items-center justify-center text-white">
        <h1 className="text-4xl font-black mb-4">Item not found.</h1>
        <Link to="/showcase" className="text-indigo-500 hover:text-indigo-400 font-bold flex items-center gap-2">
          <ArrowLeft className="w-5 h-5" /> Back to Showcase
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] pt-24 pb-20">
      <Helmet>
        <title>{item.title} | PlataPost AI</title>
        <meta name="description" content={item.description.substring(0, 160)} />
      </Helmet>

      <main className="max-w-4xl mx-auto px-6 mt-8">
        <Link to="/showcase" className="inline-flex items-center gap-2 text-gray-400 hover:text-indigo-400 transition-colors font-bold text-sm uppercase tracking-widest mb-12">
          <ArrowLeft className="w-4 h-4" />
          {isBrazil ? "Voltar ao Showcase" : "Back to Showcase"}
        </Link>

        {/* Content Layout */}
        <div className="flex flex-col gap-12">
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col gap-8"
          >
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest bg-indigo-500/10 px-3 py-1 rounded-lg border border-indigo-500/10">
                  {item.brandName}
                </span>
                <div className={`flex items-center gap-2 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${
                  item.platform === 'ig' ? 'bg-gradient-to-tr from-yellow-400/20 via-red-500/20 to-purple-600/20 text-yellow-500 border border-yellow-500/20' :
                  item.platform === 'li' ? 'bg-blue-600/20 text-blue-400 border border-blue-500/20' :
                  item.platform === 'fb' ? 'bg-[#1877F2]/20 text-[#1877F2] border border-[#1877F2]/20' :
                  item.platform === 'tr' ? 'bg-black text-white border border-white/20' :
                  'bg-gray-800 text-gray-300 border border-gray-600'
                }`}>
                  {item.platform === 'ig' && <Instagram className="w-3 h-3" />}
                  {item.platform === 'li' && <Linkedin className="w-3 h-3" />}
                  {item.platform === 'x' && <Twitter className="w-3 h-3" />}
                  {item.platform === 'fb' && <Facebook className="w-3 h-3" />}
                  {item.platform === 'tr' && <AtSign className="w-3 h-3" />}
                  {item.platform === 'ig' ? 'Instagram' : item.platform === 'li' ? 'LinkedIn' : item.platform === 'fb' ? 'Facebook' : item.platform === 'tr' ? 'Threads' : 'X'}
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-7xl font-black text-white leading-tight tracking-tighter mb-4">
                {item.title}
              </h1>
            </div>

            <div className="w-full aspect-square md:aspect-[4/3] relative rounded-[2rem] md:rounded-[3rem] overflow-hidden border border-white/5 shadow-2xl group">
              <img 
                src={item.imageUrls && item.imageUrls.length > 0 ? item.imageUrls[currentImageIndex] : item.imageUrl} 
                alt={item.title} 
                className="w-full h-full object-cover transition-opacity duration-300" 
                referrerPolicy="no-referrer" 
              />
              {item.imageUrls && item.imageUrls.length > 1 && (
                <>
                  <button onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/80 rounded-full text-white backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100">
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/80 rounded-full text-white backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100">
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-black/50 backdrop-blur-sm rounded-full">
                    {item.imageUrls.map((_, idx) => (
                      <div key={idx} className={`w-2 h-2 rounded-full transition-all ${idx === currentImageIndex ? 'bg-white scale-125' : 'bg-white/40'}`} />
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="prose prose-invert prose-indigo max-w-none prose-h1:text-3xl prose-h1:font-black prose-h1:tracking-tight prose-h2:text-2xl prose-h2:font-black prose-h2:tracking-tight prose-p:text-gray-300 prose-p:leading-relaxed prose-p:font-medium prose-a:text-indigo-400 prose-li:text-gray-300 markdown-body">
              <Markdown>{item.description}</Markdown>
            </div>
            
          </motion.div>

        </div>
      </main>
    </div>
  );
}
